<template>
  <div class="pb-6 pt-6 sm:pt-8 sm:px-8 px-4 relative w-full">

    <!-- Header -->
    <div class="bg-white shadow-sm rounded-lg px-4 sm:px-6 py-3 sticky top-14 z-10">
      <div class="max-w-screen-xl mx-auto flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div class="flex items-center gap-4 w-full sm:w-auto">
          <h1 class="text-xl md:text-2xl font-semibold text-gray-900">Leads Capturados</h1>
          <label class="inline-flex items-center text-sm text-gray-700">
            <input type="checkbox" v-model="showAll" class="form-checkbox h-4 w-4 text-green-600" />
            <span class="ml-2">Mostrar contactados</span>
          </label>
        </div>

        <div class="flex items-center gap-3 w-full sm:w-auto">
          <div class="relative w-full sm:w-64">
            <input v-model="search" type="search" placeholder="Buscar por nombre o WhatsApp" class="w-full pl-3 pr-9 py-2 border rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-emerald-300" />
            <svg class="w-4 h-4 absolute right-2 top-2.5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"/></svg>
          </div>
        </div>
      </div>
    </div>

    <!-- Tabla -->
    <div class="mt-4 bg-white shadow-sm rounded-lg sm:rounded-lg">
      <div class="max-w-screen-xl mx-auto px-2 sm:px-4 py-2">
        <div class="overflow-x-auto">
          <div class="max-h-[60vh] overflow-auto border border-gray-100 rounded">
            <div class="grid gap-3">
              <template v-if="displayedLeads.length">
                <div v-for="lead in displayedLeads" :key="lead.id" class="bg-white rounded shadow-sm p-3 flex items-center justify-between gap-3">
                  <div class="flex-1 min-w-0">
                    <div class="text-sm font-medium text-gray-900 truncate">{{ lead.nombre }}</div>
                    <div class="text-xs text-gray-500 truncate">
                      <a :href="`https://wa.me/${lead.whatsapp?.replace(/\+/g, '')}`" target="_blank" class="hover:underline text-green-600">
                        {{ lead.whatsapp }}
                      </a>
                      <span v-if="lead.email"> · {{ lead.email }}</span>
                    </div>
                    <div class="text-xs text-gray-400 mt-1">
                      {{ new Date(lead.fechaCaptura).toLocaleString('es-ES', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) }}
                      <span v-if="lead.origen"> · Origen: {{ lead.origen }}</span>
                    </div>
                  </div>
                  <div class="hidden md:flex md:flex-col md:items-end md:justify-center md:gap-1">
                    <div>
                      <span :class="lead.isContactado ? 'inline-flex items-center px-2 py-0.5 rounded text-xs bg-emerald-100 text-emerald-700' : 'inline-flex items-center px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-700'">
                        {{ lead.isContactado ? 'Contactado' : 'Pendiente' }}
                      </span>
                    </div>
                  </div>
                  <div class="flex items-center gap-2">
                    <button @click="toggleContactado(lead)" :title="lead.isContactado ? 'Marcar como pendiente' : 'Marcar como contactado'" class="p-2 rounded hover:bg-gray-100 text-sm">
                      <svg v-if="lead.isContactado" class="w-4 h-4 text-amber-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                      <svg v-else class="w-4 h-4 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/></svg>
                    </button>
                    <a :href="`https://wa.me/${lead.whatsapp?.replace(/\+/g, '')}?text=Hola ${lead.nombre}, soy de Excurix`" target="_blank" title="Abrir WhatsApp" class="p-2 rounded hover:bg-gray-100 text-sm">
                      <svg class="w-4 h-4 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    </a>
                    <button @click="deleteModal(lead.id)" title="Eliminar" class="p-2 rounded hover:bg-gray-100 text-sm">
                      <svg class="w-4 h-4 text-red-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                    </button>
                  </div>
                </div>
              </template>
              <template v-else>
                <div class="p-6 text-center text-gray-500">No hay leads para mostrar</div>
              </template>
            </div>
          </div>
        </div>
      </div>
      <!-- Pagination -->
      <div class="max-w-screen-xl mx-auto px-2 sm:px-4 py-3 flex items-center justify-between">
        <div class="text-sm text-gray-600">Mostrando {{ startIndex + 1 }} - {{ Math.min(endIndex, totalFiltered) }} de {{ totalFiltered }}</div>
        <div class="flex items-center gap-2">
          <button @click="changePage(currentPage-1)" :disabled="currentPage===1" class="px-2 py-1 rounded border bg-white disabled:opacity-50">Anterior</button>
          <div class="text-sm">Página {{ currentPage }} / {{ totalPages }}</div>
          <button @click="changePage(currentPage+1)" :disabled="currentPage===totalPages" class="px-2 py-1 rounded border bg-white disabled:opacity-50">Siguiente</button>
        </div>
      </div>
    </div>
  <Modal message="Lead" @confirm="deleteLead" ref="modalComponent" />
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import leadService from "../../../composables/api/leadService";
import Modal from "../../modalConfirmar/ModalConfirmar.vue";

export default {
  name: 'LeadsAdmin',
  components: { Modal },
  setup() {
    const leads = ref([]);
    const showAll = ref(false);
    const search = ref('');
    const pageSize = ref(10);
    const currentPage = ref(1);
    const modalComponent = ref(null);

    const filteredLeads = computed(() => {
      let filtered = leads.value;
      
      // Filtrar por contactado
      if (!showAll.value) {
        filtered = filtered.filter(l => !l.isContactado);
      }

      // Filtrar por búsqueda
      if (search.value) {
        const searchLower = search.value.toLowerCase();
        filtered = filtered.filter(l => 
          l.nombre?.toLowerCase().includes(searchLower) ||
          l.whatsapp?.toLowerCase().includes(searchLower) ||
          l.email?.toLowerCase().includes(searchLower)
        );
      }

      return filtered;
    });

    const totalFiltered = computed(() => filteredLeads.value.length);
    const totalPages = computed(() => Math.ceil(totalFiltered.value / pageSize.value) || 1);
    const startIndex = computed(() => (currentPage.value - 1) * pageSize.value);
    const endIndex = computed(() => startIndex.value + pageSize.value);
    
    const displayedLeads = computed(() => {
      return filteredLeads.value.slice(startIndex.value, endIndex.value);
    });

    const changePage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
      }
    };

    const fetchLeads = async () => {
      try {
        const fetchedLeads = await leadService.getAllLeads();
        leads.value = fetchedLeads || [];
      } catch (error) {
        console.error('Error al cargar leads:', error);
        leads.value = [];
      }
    };

    const toggleContactado = async (lead) => {
      try {
        if (lead.isContactado) {
          // Si ya está contactado, no hay endpoint para revertir, solo actualizar localmente
          lead.isContactado = false;
        } else {
          await leadService.markAsContacted(lead.id);
          lead.isContactado = true;
        }
      } catch (error) {
        console.error('Error al actualizar estado:', error);
      }
    };

    const deleteLead = async (leadId) => {
      try {
        await leadService.removeLead(leadId);
        await fetchLeads();
      } catch (error) {
        console.error('Error al eliminar lead:', error);
      }
    };

    const deleteModal = (leadId) => {
      if (modalComponent.value) {
        modalComponent.value.getModal(leadId);
      }
    };

    onMounted(() => {
      fetchLeads();
    });

    return {
      leads,
      showAll,
      search,
      displayedLeads,
      currentPage,
      totalPages,
      startIndex,
      endIndex,
      totalFiltered,
      changePage,
      toggleContactado,
      deleteLead,
      deleteModal,
      modalComponent,
    };
  }
};
</script>
